import React, { useRef } from 'react';
import { ShareModal } from './ShareModal';
import { useI18n } from '../i18n';
import { buildShareText, type ShareEntity } from '../utils/roomShare';
import type { ShareSurface } from '../services/shareAnalytics';

interface EntityShareModalProps {
  /** What to share; `null` keeps the sheet closed. */
  entity: ShareEntity | null;
  surface: ShareSurface;
  onClose: () => void;
}

/**
 * CORE-786 — the «Поделиться» popup for a list of cards.
 *
 * A screen that shares many entities (the /lives and /practices grids, the
 * calendar) keeps one `ShareEntity | null` in state and renders this once;
 * `shareEntityOrModal(entity, surface, () => setEntity(entity))` on the button
 * decides between the OS sheet and this popup. The text is the same localized
 * template the sheet gets (`buildShareText`), so both channels say one thing.
 */
export const EntityShareModal: React.FC<EntityShareModalProps> = ({ entity, surface, onClose }) => {
  const { i18n } = useI18n();
  // Keep the last entity through the closing fade so the sheet doesn't blank
  // out while it animates away.
  const shown = useRef<ShareEntity | null>(null);
  if (entity) shown.current = entity;
  const current = shown.current;
  if (!current) return null;

  const { subject, message, url } = buildShareText(current);
  return (
    <ShareModal
      visible={!!entity}
      onClose={onClose}
      url={url}
      title={subject}
      subject={subject}
      message={message}
      language={i18n.language}
      analytics={{ entityType: current.type, entityId: current.id, surface }}
    />
  );
};

export default EntityShareModal;
